import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@component/dialog";
import { Button } from "@component/button";
import { Tabs, TabsContent } from "@component/tabs";
import { Label } from "@component/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { CreditCardIcon, GlobeIcon, CheckCircleIcon, ExternalLinkIcon, UnlinkIcon, MapPinIcon, LinkIcon } from "lucide-react";
import { getStripeAccount, stripeAccountConnect, stripeConnectOauth, stripeDisconnect } from "@service/payment";
import { StripeAccountInfo } from "@type/stripe";
import { useQuery } from "@util/useQuery";
import { useSearchParams } from 'react-router-dom'

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export default function PaymentSettingsDialog(props: Props) {
    const params = useQuery()
    const [isOpen, setOpen] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<string>("payment-method");
    const [currency, setCurrency] = useState<string>("USD");
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [showStripeConnect, setShowStripeConnect] = useState(false);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isConnectingStripe, setIsConnectingStripe] = useState<boolean>(false);
    const [isIsDisconnectingStripe, setDisconnectingStripe] = useState<boolean>(false);
    const [isStripeConnected, setStripeConnected] = useState<boolean>(false);
    const [isCompletedStripeOnboarding, setCompletedStripeOnboarding] = useState<boolean>(false);
    const [stripeAccountInfo, setStripeAccountInfo] = useState<StripeAccountInfo | undefined>(undefined)

    useEffect(() => {
        if (props.isOpen) {
            setLoading(true);
            getStripeAccount().then((response) => {
                if (response.data.data.account_id !== "") {
                    setStripeAccountInfo(response.data.data);
                    setCompletedStripeOnboarding(!response.data.data.onboard_url);
                    setStripeConnected(true);
                } else {
                    setStripeConnected(false);
                    setStripeAccountInfo(undefined);
                    setCompletedStripeOnboarding(false);
                }
            }).finally(() => {
                setLoading(false);
            })
        }

        return () => {
            setStripeConnected(false);
            setStripeAccountInfo(undefined);
            setCompletedStripeOnboarding(false);
            setLoading(false);
        }
    }, [props.isOpen])

    useEffect(() => {
        if (searchParams.get("code")) {
            setOpen(true);
            setLoading(true);
            stripeConnectOauth(searchParams.get("code") || "").then((response) => {
                if (response.data.data.account_id !== "") {
                    getStripeAccount().then((response) => {
                        if (response.data.data.account_id !== "") {
                            setStripeAccountInfo(response.data.data);
                            setCompletedStripeOnboarding(true);
                            setStripeConnected(true);
                        } else {
                            setStripeConnected(false);
                            setStripeAccountInfo(undefined);
                        }
                    }).finally(() => {
                        setLoading(false);
                        params.delete('scope')
                        params.delete('code')
                        setSearchParams(params.toString());
                    })
                }
            })
        }
    }, [searchParams.get("code")])

    const handleConnectStripe = () => {
        setIsConnectingStripe(true);

        stripeAccountConnect(selectedCountry || "").then((response) => {
            window.location.href = response.data.data.url
        })
    };

    const handleDisconnectStripe = () => {
        setDisconnectingStripe(true);
        stripeDisconnect().then(() => {
            setStripeConnected(false);
            setStripeAccountInfo(undefined);
            setCompletedStripeOnboarding(false);
        }).finally(() => {
            setDisconnectingStripe(false);
        })
    }

    const handleNextStep = () => {
        setShowStripeConnect(true);
    }

    return (
        <Dialog open={isOpen || props.isOpen} onOpenChange={() => {
            props.onClose();
            setOpen(false);
        }}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Payment Settings</DialogTitle>
                    <DialogDescription>
                        Configure your payment methods and preferences.
                    </DialogDescription>
                </DialogHeader>

                <Tabs
                    defaultValue="payment-method"
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    {isLoading ? (
                        <div className="flex flex-row justify-center p-2">
                            <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : (
                        <>
                            <TabsContent value="payment-method" className="space-y-4">
                                {isStripeConnected ? (
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <div className={`flex ${isStripeConnected ? "flex-col" : "flex-row justify-between"} space-y-6`}>
                                            <div className="flex items-center space-x-3">
                                                <div
                                                    className={`h-10 w-10 ${isStripeConnected ? "bg-green-100" : "bg-blue-100"} rounded-full flex items-center justify-center flex-shrink-0`}
                                                >
                                                    <CreditCardIcon
                                                        className={`h-5 w-5 ${isStripeConnected ? "text-green-600" : "text-blue-600"}`}
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center">
                                                        <h3 className="font-medium">Stripe Account</h3>
                                                        <span className="ml-2 flex items-center text-sm text-green-600">
                                                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                            Connected
                                                        </span>
                                                    </div>
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        <p>Connected as: {stripeAccountInfo?.email || ""}</p>
                                                        <p className="mt-1">
                                                            Account ID: {stripeAccountInfo?.account_id || ""}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            {isCompletedStripeOnboarding ? (
                                                <div className="flex flex-row space-x-2 pt-2 w-full">
                                                    {stripeAccountInfo?.manage_url !== "" && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="flex items-center justify-center w-full"
                                                            onClick={() => {
                                                                window.open(stripeAccountInfo?.manage_url || "");
                                                            }}
                                                        >
                                                            <ExternalLinkIcon className="h-4 w-4 mr-1" />
                                                            Manage
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="flex items-center justify-center w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                                        onClick={handleDisconnectStripe}
                                                        disabled={isIsDisconnectingStripe}
                                                    >
                                                        <UnlinkIcon className="h-4 w-4 mr-1" />
                                                        {isIsDisconnectingStripe
                                                            ? "Disconnecting..."
                                                            : "Disconnect"}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    className="flex items-center justify-center w-full"
                                                    onClick={() => {
                                                        window.location.href = stripeAccountInfo?.onboard_url || "";
                                                    }}
                                                >
                                                    Complete Stripe Onboarding
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ) : !showStripeConnect ? (
                                    <>
                                        <div className="flex items-start space-x-3 mb-4">
                                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <MapPinIcon className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium">Select Your Country</h3>
                                                <p className="text-sm text-gray-500 -mt-0.5">
                                                    Choose your country to continue with Stripe connection.
                                                </p>
                                            </div>
                                        </div>

                                        <Select
                                            value={selectedCountry || ""}
                                            onValueChange={setSelectedCountry}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue placeholder="Select a country" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="SG">Singapore</SelectItem>
                                                <SelectItem value="MY">Malaysia</SelectItem>
                                            </SelectContent>
                                        </Select>

                                        <div className="flex justify-end pt-4">
                                            <Button
                                                variant="default"
                                                onClick={handleNextStep}
                                                disabled={!selectedCountry}
                                            >
                                                Continue to Connect Stripe
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-start space-x-3 mb-4">
                                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <CreditCardIcon className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium">Connect Stripe Account</h3>
                                                <p className="text-sm text-gray-500 -mt-0.5">
                                                    Connect your Stripe account for{" "}
                                                    {selectedCountry === "SG" ? "Singapore" : "Malaysia"}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                                            <p className="text-sm text-gray-600 mb-4">
                                                You'll be redirected to Stripe to complete the connection
                                                process. After connecting, you'll be able to receive payments
                                                through our platform.
                                            </p>

                                            <div className="flex space-x-3 pt-2">
                                                <Button
                                                    variant="outline"
                                                    disabled={isConnectingStripe}
                                                    onClick={() => setShowStripeConnect(false)}
                                                    className="flex-1"
                                                >
                                                    Back
                                                </Button>
                                                <Button
                                                    disabled={isConnectingStripe}
                                                    onClick={handleConnectStripe}
                                                    className="flex-1"
                                                >
                                                    {isConnectingStripe ? (
                                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : <LinkIcon className="h-4 w-4 mr-1" />}
                                                    Connect Stripe
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className={`flex ${isStripeConnected ? "flex-col" : "flex-row justify-between"} space-y-6`}>
                                        <div className="flex items-center space-x-3">
                                            <div
                                                className={`h-10 w-10 ${isStripeConnected ? "bg-green-100" : "bg-blue-100"} rounded-full flex items-center justify-center flex-shrink-0`}
                                            >
                                                <CreditCardIcon
                                                    className={`h-5 w-5 ${isStripeConnected ? "text-green-600" : "text-blue-600"}`}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center">
                                                    <h3 className="font-medium">Connect Stripe Account</h3>
                                                    {(isCompletedStripeOnboarding && isStripeConnected) && (
                                                        <span className="ml-2 flex items-center text-sm text-green-600">
                                                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                            Connected
                                                        </span>
                                                    )}
                                                </div>
                                                {(isCompletedStripeOnboarding && isStripeConnected) ? (
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        <p>Connected as: {stripeAccountInfo?.email || ""}</p>
                                                        <p className="mt-1">
                                                            Account ID: {stripeAccountInfo?.account_id || ""}
                                                        </p>
                                                    </div>
                                                ) : (!isStripeConnected) ? (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        Connect your Stripe account to receive payments
                                                    </p>
                                                ) : (!isCompletedStripeOnboarding) && (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        Please complete the onboarding to successfully connect your Stripe account.
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {isStripeConnected ? (
                                            <>
                                                {isCompletedStripeOnboarding ? (
                                                    <div className="grid grid-cols-2 gap-3 pt-2 w-full">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="flex items-center justify-center w-full"
                                                            onClick={() => {
                                                                window.open(stripeAccountInfo?.manage_url || "");
                                                            }}
                                                        >
                                                            <ExternalLinkIcon className="h-4 w-4 mr-1" />
                                                            Manage
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="flex items-center justify-center w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                                            onClick={() => { }}
                                                            disabled
                                                        >
                                                            <UnlinkIcon className="h-4 w-4 mr-1" />
                                                            {isConnectingStripe
                                                                ? "Disconnecting..."
                                                                : "Disconnect"}
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        className="flex items-center justify-center w-full"
                                                        onClick={() => {
                                                            window.location.href = stripeAccountInfo?.onboard_url || "";
                                                        }}
                                                    >
                                                        Complete Stripe Onboarding
                                                    </Button>
                                                )}
                                            </>
                                        ) : (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="flex items-center"
                                                disabled={isConnectingStripe}
                                                onClick={handleConnectStripe}
                                            >
                                                {isConnectingStripe && (
                                                    <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                )}
                                                Connect
                                            </Button>
                                        )}
                                    </div>
                                </div> */}
                            </TabsContent>

                            <TabsContent value="currency" className="space-y-4">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <GlobeIcon className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium">Preferred Currency</h3>
                                        <p className="text-sm text-gray-500">
                                            Select the currency you want to receive payments in.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="currency">Currency</Label>
                                    <Select value={currency} onValueChange={setCurrency}>
                                        <SelectTrigger id="currency">
                                            <SelectValue placeholder="Select currency" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="USD">USD - US Dollar</SelectItem>
                                            <SelectItem value="EUR">EUR - Euro</SelectItem>
                                            <SelectItem value="GBP">GBP - British Pound</SelectItem>
                                            <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                                            <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                                            <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </TabsContent>
                        </>
                    )}
                </Tabs>

                {/* <DialogFooter className="flex space-x-2 justify-end">
                    <Button variant="outline" onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} style={{ backgroundColor: "#0C49FFFF" }}>
                        Save Changes
                    </Button>
                </DialogFooter> */}
            </DialogContent>
        </Dialog >
    );
}
